import {styled} from '@mui/material/styles'
import FormControl from '@mui/material/FormControl'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  tooltipClasses,
  Typography
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import ReportIcon from '@mui/icons-material/Report'
import BookmarksOutlinedIcon from '@mui/icons-material/BookmarksOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

export const FILTERS_DRAWER_WIDTH = 400

const PREFIX = 'FilterPanelComponent'

export const classes = {
  root: `${PREFIX}-root`,
  drawer: `${PREFIX}-drawer`,
  drawerPaper: `${PREFIX}-drawerPaper`,
  mainFiltersContainer: `${PREFIX}-mainFiltersContainer`,
  title: `${PREFIX}-title`,
  filterSubsectionContainer: `${PREFIX}-filterSubsectionContainer`,
  selectRoot: `${PREFIX}-selectRoot`,
  loadSnapshotButtonContainer: `${PREFIX}-loadSnapshotButtonContainer`,
  loadSnapshotButton: `${PREFIX}-loadSnapshotButton`,
  checkbox: `${PREFIX}-checkbox`,
  checkboxesContainer: `${PREFIX}-checkboxesContainer`,
  closeButton: `${PREFIX}-closeButton`,
  filterTitle: `${PREFIX}-filterTitle`,
  filtersRenderDiv: `${PREFIX}-filtersRenderDiv`,
  rangeLabel: `${PREFIX}-rangeLabel`,
  radioLabel: `${PREFIX}-radioLabel`,
  typographyS: `${PREFIX}-typographyS`
}

export const Root = styled('div')(({theme}) => ({
  [`& .${classes.root}`]: {
    display: 'flex'
  },

  [`& .${classes.drawer}`]: {
    width: FILTERS_DRAWER_WIDTH,
    flexShrink: 0
  },

  [`& .${classes.drawerPaper}`]: {
    width: FILTERS_DRAWER_WIDTH,
    marginLeft: 64,
    marginTop: 41,
    height: 'calc(100vh - 40px)',
    overflowX: 'hidden',
    boxShadow: theme.shadows[4]
  },

  [`& .${classes.mainFiltersContainer}`]: {
    paddingTop: theme.spacing(1.5),
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center'
  },

  [`& .${classes.title}`]: {
    marginBottom: theme.spacing(2)
  },

  [`& .${classes.filterSubsectionContainer}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },

  [`& .${classes.filterRangeSubsectionContainer}`]: {
    margin: theme.spacing(0, 2, 0, 2),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },

  [`& .${classes.selectRoot}`]: {
    width: '80%'
  },

  [`& .${classes.loadSnapshotButtonContainer}`]: {
    padding: theme.spacing(2),
    alignSelf: 'center'
  },

  [`& .${classes.loadSnapshotButton}`]: {
    background: theme.palette.hpPrimaryDark,
    '&:hover': {
      background: theme.palette.hpPrimary
    }
  },

  [`& .${classes.checkbox}`]: {
    color: theme.palette.hpPrimaryDark,
    '&.Mui-checked': {
      color: theme.palette.hpPrimaryDark
    },
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: theme.typography.fontWeightRegular
  },

  [`& .${classes.checkboxesContainer}`]: {
    paddingLeft: theme.spacing(2)
  },

  [`& .${classes.closeButton}`]: {
    padding: 0
  },

  [`& .${classes.filterTitle}`]: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.subtitle1.fontSize,
    color: theme.palette.hpAccentBlack
  },
  [`& .${classes.filtersRenderDiv}`]: {
    display: 'flex',
    flexDirection: 'column'
  },

  [`& .${classes.rangeLabel}`]: {
    fontSize: theme.typography.h1.fontSize,
    fontWeight: theme.typography.fontWeightRegular,
    color: theme.palette.hpBlack,
    marginBottom: '16px',
    marginTop: '12px'
  },
  [`& .${classes.radioLabel}`]: {
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: theme.typography.fontWeightRegular,
    color: theme.palette.hpAccentBlack
  },
  [`& .${classes.typographyS}`]: {
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: theme.typography.fontWeightSemibold
  }
}))

export const StyledDivider = styled(Divider)(() => ({
  marginTop: '8px',
  marginBottom: '8px'
}))

export const StyledSaveDialogTitle = styled(DialogTitle)(({theme}) => ({
  paddingBottom: 0,
  alignItems: 'center',
  display: 'flex'
}))

export const StyledSavedFilterDialogHeaderText = styled(Typography)(({theme}) => ({
  fontSize: theme.typography?.titleMedium?.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.hpBlack
}))

export const StyledStickyFooterBox = styled(Box)(({theme}) => ({
  flexShrink: 0,
  position: 'sticky',
  bottom: 0,
  backgroundColor: theme.palette.background.paper,
  zIndex: 5
}))

export const StyledSaveFiltersPaper = styled(Paper)(({theme}) => ({
  padding: theme.spacing(1.5),
  elevation: 26
}))
export const StyledAppliedSaveFiltersPaper = styled(Paper)(({theme}) => ({
  padding: '8px',
  backgroundColor: theme.palette.success.light,
  elevation: 26
}))

export const StyledSaveFiltersStack = styled(Stack)({
  flexDirection: 'row',
  spacing: 1,
  alignItems: 'center',
  justifyContent: 'space-between'
})

export const StyledClearAllButton = styled(Button)(({theme}) => ({
  '&:hover': {
    backgroundColor: theme.palette.error?.light,
    border: 'none'
  }
}))

export const StyledSaveFiltersOutlinedButton = styled(Button)(({theme}) => ({
  fontWeight: theme.typography.fontWeightMedium,
  '&:hover': {
    backgroundColor: 'transparent',
    borderColor: theme.palette.primary.main
  },
  '&:disabled': {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.hpBase,
    borderColor: theme.palette.grey[300]
  }
}))

export const StyledSaveFiltersTextField = styled(TextField)({
  marginTop: '16px',
  '& .MuiOutlinedInput-root': {
    height: '50px'
  },
  '& .MuiInputBase-input': {
    padding: '16.5px 14px'
  }
})

export const SuccessAlert = styled(Alert)(({theme}) => ({
  padding: '10px',
  paddingLeft: '8px',
  paddingRight: '8px',
  width: '100%',
  backgroundColor: theme.palette.success.light,
  color: theme.palette.hpBlack
}))

export const BoldCheckIcon = styled(CheckIcon)({
  strokeWidth: 2,
  stroke: 'currentColor'
})

const StyledDialog = styled(Dialog)(({theme}) => ({
  '& .MuiDialog-paper': {
    padding: 0,
    maxWidth: '55vw',
    minWidth: '650px'
  },
  '& .MuiDialogTitle-root': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
    cursor: 'default'
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(1)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
    marginTop: theme.spacing(1)
  }
}))

export const StyledDialogContent = styled(DialogContent)(({theme}) => ({
  marginLeft: theme.spacing(3.5)
}))

export const StyledDialogTitle = styled(DialogTitle)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  userSelect: 'none',
  WebkitUserSelect: 'none',
  MozUserSelect: 'none',
  msUserSelect: 'none',
  cursor: 'default'
}))

export const StyledReportIcon = styled(ReportIcon)(({theme}) => ({
  color: theme.palette.hpAccentOrange,
  cursor: 'default'
}))

export const StyledDisclaimerText = styled(Typography)(({theme}) => ({
  fontWeight: theme.typography.body2,
  fontSize: theme.typography.body1.fontSize,
  color: theme.palette.hpAccentBlack,
  paddingRight: theme.spacing(2),
  marginLeft: theme.spacing(2.5),
  userSelect: 'none',
  WebkitUserSelect: 'none',
  MozUserSelect: 'none',
  msUserSelect: 'none',
  cursor: 'default'
}))

export const StyledDialogActions = styled(DialogActions)(({theme}) => ({
  paddingLeft: theme.spacing(3.2),
  padding: theme.spacing(1),
  marginTop: theme.spacing(1)
}))

export const StyledDialogButton = styled(Button)(({theme, type}) => ({
  width: '20%',
  marginLeft: theme.spacing(1),
  marginBottom: theme.spacing(1),
  '&:hover': {
    backgroundColor: type === 'cancel' ? theme.palette.grey[50] : theme.palette.error?.main,
    borderColor: 'inherit'
  }
}))

export const StyledSavedFiltersIconButton = styled(IconButton)(({theme}) => ({
  border: `1px solid ${theme.palette.hpAccentGreenVivid}`,
  borderRadius: theme.spacing(0.5),
  '&:disabled': {
    border: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.hpBase,
    '&:hover': {
      cursor: 'not-allowed'
    }
  }
}))

export const StyledSavedFiltersBookmarkIcon = styled(BookmarksOutlinedIcon)(({theme, disabled}) => ({
  color: disabled ? theme.palette.hpBase : theme.palette.hpAccentGreenVivid,
  transition: 'color 0.2s ease'
}))

export const StyledSavedFiltersDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    minWidth: '400px',
    maxWidth: '40vw'
  }
})

export const StyledSpaceBox = styled(Box)(() => ({
  height: '20px'
}))

export const StyledSavedFiltersDialogContent = styled(DialogContent)(({theme}) => ({
  paddingLeft: theme.spacing(4),
  overflowY: 'auto',
  maxHeight: '30vh'
}))

export const StyledSavedFiltersFormControl = styled(FormControl)({
  display: 'flex'
})

export const StyledSavedFiltersDialogActions = styled(DialogActions)(({theme}) => ({
  paddingTop: theme.spacing(1)
}))

export const StyledSavedFiltersCancelButton = styled(Button)({
  '&:hover': {
    backgroundColor: 'transparent'
  }
})

export const StyledCircularProgress = styled(CircularProgress)(({theme}) => ({
  display: 'block',
  margin: '5px auto'
}))

export const StyledSavedFiltersApplyButton = styled(Button)({
  '&:hover': {
    cursor: 'pointer'
  }
})

export const StyledSavedFiltersInfoIcon = styled(InfoOutlinedIcon)(({theme}) => ({
  fontSize: theme.typography.h6.fontSize,
  color: theme.palette.hpBaseDark
}))

export const StyledSavedFiltersStackItem = styled(Stack)({
  justifyContent: 'space-between'
})

export const StyledHtmlTooltip = styled(({className, ...props}) => (
  <Tooltip {...props} classes={{popper: className}} />
))(({theme}) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.hpBaseDarker,
    color: theme.palette.hpWhite,
    fontSize: theme.typography.body2.fontSize,
    width: 'auto',
    maxWidth: 250,
    alignItems: 'center'
  }
}))

export const StyledBackIconButton = styled(IconButton)({
  marginBottom: 1,
  paddingLeft: 0,
  '&:hover': {
    backgroundColor: 'transparent'
  }
})

export const StyledSaveFiltersDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    maxWidth: '444px',
    width: '100%'
  }
})

export const StyledSaveFiltersDialogActions = styled(DialogActions)({
  padding: '16px'
})

export const StyledSaveFiltersSaveButton = styled(Button)({
  '&:hover': {
    cursor: 'pointer'
  }
})

export const StyledSavedFiltersMoreIconButton = styled(IconButton)({
  marginRight: -12
})

export const StyledTooltipList = styled('ul')({
  listStyleType: 'none',
  paddingLeft: 0,
  margin: 0
})

export const AppliedFilterBox = styled(Box)(({theme}) => ({
  flexShrink: 0,
  position: 'sticky',
  bottom: 0,
  backgroundColor: theme.palette.background.paper,
  zIndex: 5
}))

export const AppliedFilterPaper = styled(Paper)(({theme}) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.success.light
}))

export const StyledAppliedFilterChip = styled(Chip)(({theme}) => ({
  marginLeft: '8px',
  paddingLeft: '8px',
  paddingRight: '8px',
  borderRadius: '4px',
  backgroundColor: theme.palette.hpWhite,
  border: 'none',
  '& .MuiChip-deleteIcon': {
    color: theme.palette.hpBaseDark, // ✅ Custom delete icon color
    fontSize: 20
  }
}))

export const StyledTooltipListItem = styled('li')(({theme}) => ({
  color: theme.palette.hpWhite
}))

export const StyledFiltersListItem = styled('li')(({theme, disabled}) => ({
  color: theme.palette.hpWhite,
  opacity: disabled ? 0.5 : 1
}))

export {StyledDialog}
